import {useVariableSuggestions} from 'hooks/useVariableSuggestions'
import {FestiveGreetingVariant} from 'models/festiveGreeting'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {fetchLanguageList} from 'utils/api/auth/productPromotionService'

type LanguageItem = {
 language: string
 language_code: string
}
type FormState = {
 language: LanguageItem | null
 template_text: string
}

const VARIABLE_OPTIONS = [
 'fpo_name',
 'fpo_centre_address',
 'aggregator_phone_number',
]
const FestiveDialog = ({
 onClose,
 onSubmit,
 templates,
 editData,
}: {
 onClose: () => void
 onSubmit: (
  data: Omit<FestiveGreetingVariant, 'id' | 'temp_id'> & {
   id?: number
  }
 ) => void
 templates: FestiveGreetingVariant[]
 editData?: FestiveGreetingVariant | null
}) => {
 const {t} = useTranslation()
 const [open, setOpen] = useState(false)
 const [form, setForm] = useState<FormState>({
  language: null,
  template_text: editData?.template_text ?? '',
 })
 const isValid =
  !!form.language && form.template_text.trim()

 const [languages, setLanguages] = useState<LanguageItem[]>(
  []
 )

 const {
  textareaRef,
  debounceRef,
  showSuggestions,
  filteredOptions,
  dropdownPos,
  handleTextChange,
  applySuggestion,
  setShowSuggestions,
  handleKeyDown,
  activeIndex,
  setActiveIndex,
 } = useVariableSuggestions({
  options: VARIABLE_OPTIONS,
 })

 const handleSubmit = () => {
  if (!form.language) return

  onSubmit({
   id: editData?.id,
   language: form.language.language,
   language_code: form.language.language_code,
   template_text: form.template_text,
  })
 }
 useEffect(() => {
  const timeoutId = debounceRef.current
  return () => {
   if (timeoutId) {
    clearTimeout(timeoutId)
   }
  }
 }, [debounceRef])

 useEffect(() => {
  const loadLanguages = async () => {
   const res = await fetchLanguageList()

   const usedLanguageCodes = new Set(
    templates.map((t) => t.language_code).filter(Boolean)
   )

   const filtered = res.filter((lang) => {
    if (editData?.language_code === lang.language_code) {
     return true
    }

    return !usedLanguageCodes.has(lang.language_code)
   })

   setLanguages(filtered)
  }

  void loadLanguages()
 }, [templates, editData])

 useEffect(() => {
  if (!editData || languages.length === 0) return

  const selectedLanguage = languages.find(
   (l) => l.language_code === editData.language_code
  )

  if (!selectedLanguage) return

  setForm({
   language: selectedLanguage,
   template_text: editData.template_text,
  })
 }, [editData, languages])

 return (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
   <div className='relative w-[520px] rounded-2xl bg-white p-6'>
    <div
     onClick={onClose}
     className='absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full hover:bg-[#F3F4F6]'
    >
     ✕
    </div>
    <h2 className='mb-6 text-lg font-semibold'>
     {t('festiveGreeting.addTemplate')}
    </h2>
    <div className='space-y-5'>
     <div
      className={`rounded-xl border px-4 py-3 relative ${
       form.language?.language ? 'bg-[#F9FAFB]' : 'bg-white'
      }`}
     >
      <p className='text-xs font-medium text-[#3F76FF] mb-1'>
       {t('templateDialog.language')}
       <span className='text-red-500'>*</span>
      </p>

      <button
       type='button'
       onClick={() => !form.language && setOpen(!open)}
       className='flex w-full items-center justify-between bg-transparent text-left outline-none'
      >
       <span
        className={
         form.language?.language
          ? 'text-gray-900'
          : 'text-gray-400'
        }
       >
        {form.language?.language ??
         t('templateDialog.selectLanguage')}
       </span>

       <svg
        className={`h-4 w-4 transition-transform ${
         open ? 'rotate-180' : ''
        }`}
        viewBox='0 0 20 20'
        fill='none'
        stroke='currentColor'
       >
        <path d='M6 8l4 4 4-4' strokeWidth='2' />
       </svg>
      </button>

      {open && (
       <div className='absolute left-0 top-full z-10 mt-2 w-full rounded-xl bg-[#F5F8FA] border border-[#E6EBF0] shadow-lg'>
        {languages.map((lang, index) => (
         <div
          key={lang.language_code}
          onMouseDown={() => {
           setForm({...form, language: lang})
           setOpen(false)
          }}
          className={`flex cursor-pointer items-center gap-3 px-4 py-4 hover:bg-gray-100 ${
           index !== languages.length - 1
            ? 'border-b border-[#E6EBF0]'
            : ''
          }`}
         >
          <span
           className={`flex h-5 w-5 items-center justify-center rounded-full border-2 
             ${
              form.language?.language_code ===
              lang.language_code
               ? 'border-blue-500'
               : 'border-gray-300'
             }
           `}
          >
           {form.language?.language_code ===
            lang.language_code && (
            <span className='h-2.5 w-2.5 rounded-full bg-blue-500' />
           )}
          </span>

          <span>{lang.language}</span>
         </div>
        ))}
       </div>
      )}
     </div>
     <div className='relative'>
      <div
       className={`rounded-xl border px-4 py-3 ${
        form.template_text ? 'bg-[#F9FAFB]' : 'bg-white'
       }`}
      >
       <p className='text-xs font-medium text-[#3F76FF] mb-1'>
        {t('templateDialog.templateText')}
        <span className='text-red-500'>*</span>
       </p>

       <textarea
        ref={textareaRef}
        rows={4}
        maxLength={200}
        value={form.template_text}
        onChange={(e) =>
         handleTextChange(
          e.target.value,
          e.target.selectionStart || 0,
          (val) => setForm({...form, template_text: val})
         )
        }
        onKeyDown={(e) =>
         handleKeyDown(e, form.template_text, (val) =>
          setForm({...form, template_text: val})
         )
        }
        onBlur={(e) => {
         if (e.relatedTarget === null) {
          setTimeout(() => setShowSuggestions(false), 150)
         }
        }}
        placeholder={t('templateDialog.enterTemplateText')}
        className='w-full resize-none bg-transparent outline-none text-gray-900 placeholder-gray-400'
       />
      </div>

      {showSuggestions && filteredOptions.length > 0 && (
       <div
        style={{
         position: 'absolute',
         top: dropdownPos.top,
         left: dropdownPos.left,
        }}
        className='z-50'
       >
        <div className='bg-white border border-[#E6EBF0] rounded-xl shadow-lg overflow-hidden min-w-[220px]'>
         {filteredOptions.map((option, index) => (
          <div
           key={option}
           onMouseEnter={() => setActiveIndex(index)}
           onMouseDown={() => {
            const updated = applySuggestion(
             form.template_text,
             option
            )
            setForm({...form, template_text: updated})
           }}
           className={`px-4 py-2 cursor-pointer ${
            index === activeIndex ? 'bg-[#F5F8FA]' : ''
           }`}
          >
           {option}
          </div>
         ))}
        </div>
       </div>
      )}
     </div>
    </div>

    <button
     onClick={() => {
      if (!isValid) return
      handleSubmit()
      onClose()
     }}
     className={`mt-8 w-full rounded-xl py-3 text-sm font-medium ${
      isValid
       ? 'bg-[#5E7CE2] text-white'
       : 'bg-[#F1F1F1] text-[#9CA3AF] cursor-not-allowed'
     }`}
    >
     {t('templateDialog.saveTemplate')}
    </button>
   </div>
  </div>
 )
}

export default FestiveDialog
